import type { AuthorizationServerMetadata, Did, DPoPKey, Session, SimpleStore } from './types';
import { locks } from './utils';

export interface OAuthDatabaseOptions {
	name: string;
}

interface SchemaItem<T> {
	value: T;
	expiresAt: number | null;
	updatedAt?: number;
}

interface Schema {
	sessions: {
		key: Did;
		value: Session;
	};
	states: {
		key: string;
		value: {
			dpopKey: DPoPKey;
			metadata: AuthorizationServerMetadata;
			verifier?: string;
		};
	};
	dpopNonces: {
		key: string;
		value: string;
	};
}

const parse = (raw: string | null) => {
	if (raw != null) {
		try {
			const parsed = JSON.parse(raw);
			if (parsed != null) {
				return parsed;
			}
		} catch {}
	}

	return {};
};

export type OAuthDatabase = ReturnType<typeof createOAuthDatabase>;

/**
 * A small localStorage-backed store, keyed per logical table. Sessions persist
 * indefinitely (until logout) so long as they carry a refresh token, which is
 * what keeps users signed in across reloads and over long periods.
 */
export const createOAuthDatabase = ({ name }: OAuthDatabaseOptions) => {
	const controller = new AbortController();
	const signal = controller.signal;

	const createStore = <N extends keyof Schema>(
		subname: N,
		getExpiry: (item: Schema[N]['value']) => null | number,
		persistUpdatedAt = false,
	): SimpleStore<Schema[N]['key'], Schema[N]['value']> => {
		let store: any;

		const storageKey = `${name}:${subname}`;

		const persist = () => store && localStorage.setItem(storageKey, JSON.stringify(store));
		const read = () => {
			if (signal.aborted) {
				throw new Error(`store closed`);
			}

			return (store ??= parse(localStorage.getItem(storageKey)));
		};

		{
			const listener = (ev: StorageEvent) => {
				if (ev.key === storageKey) {
					store = undefined;
				}
			};

			globalThis.addEventListener('storage', listener, { signal });
		}

		{
			const cleanup = async (lock: Lock | true | null) => {
				if (!lock || signal.aborted) {
					return;
				}

				await new Promise((resolve) => setTimeout(resolve, 10_000));
				if (signal.aborted) {
					return;
				}

				const now = Date.now();
				let changed = false;

				read();

				for (const key in store) {
					const item = store[key];
					const expiresAt = item.expiresAt;

					if (expiresAt !== null && now > expiresAt) {
						changed = true;
						delete store[key];
					}
				}

				if (changed) {
					persist();
				}
			};

			if (locks) {
				locks.request(`${storageKey}:cleanup`, { ifAvailable: true }, cleanup);
			} else {
				cleanup(true);
			}
		}

		return {
			get(key) {
				read();

				const item: SchemaItem<Schema[N]['value']> = store[key];
				if (!item) {
					return;
				}

				const expiresAt = item.expiresAt;
				if (expiresAt !== null && Date.now() > expiresAt) {
					delete store[key];
					persist();

					return;
				}

				return item.value;
			},
			getWithLapsed(key) {
				read();

				const item: SchemaItem<Schema[N]['value']> = store[key];
				const now = Date.now();
				if (!item) {
					return [undefined, Infinity];
				}

				const updatedAt = item.updatedAt;
				if (updatedAt === undefined) {
					return [item.value, Infinity];
				}

				return [item.value, now - updatedAt];
			},
			set(key, value) {
				read();

				const item: SchemaItem<Schema[N]['value']> = {
					value: value,
					expiresAt: getExpiry(value),
					updatedAt: persistUpdatedAt ? Date.now() : undefined,
				};

				store[key] = item;
				persist();
			},
			delete(key) {
				read();

				if (store[key] !== undefined) {
					delete store[key];
					persist();
				}
			},
			keys() {
				read();

				return Object.keys(store);
			},
		};
	};

	return {
		dispose: () => {
			controller.abort();
		},

		sessions: createStore('sessions', ({ token }) => {
			// Keep refresh-capable sessions forever; they're how we stay logged in.
			if (token.refresh) {
				return null;
			}

			return token.expires_at ?? null;
		}),
		// Authorization requests are short-lived.
		states: createStore('states', () => Date.now() + 10 * 60 * 1_000),
		// DPoP nonces are cached for 24 hours.
		dpopNonces: createStore('dpopNonces', () => Date.now() + 24 * 60 * 60 * 1_000, true),
		inflightDpop: new Map<string, PromiseWithResolvers<void>>(),
	};
};
